import { CLICK_EVENTS, trackClick } from '@/services/activityTracker/activityTrackerHelper';
import { ChecklistTemplateEditable } from '@/ui/DTO';
import { create, head, remove, update } from '@/services/api/resources/checklistTemplate';
import { pushRoute } from '@/ui/helpers';
import { Right } from 'fp-ts/lib/Either';

interface BaseTemplate {
  id: number;
  title: string;
  isFeatured: boolean;
}

export const goToCustomizeExistingTemplate = (template: BaseTemplate) => {
  trackClick({
    ...CLICK_EVENTS.CUSTOMIZE_READY_MADE_TEMPLATE,
    'template-name': template.title,
    'is-suggested': template.isFeatured,
  });

  pushRoute({
    name: 'checklistCreate',
    query: { fromTplId: template.id.toString() },
  });
};

export const createChecklistTemplates = async (templates: ChecklistTemplateEditable[]) => {
  const promises = [];
  const onlyCreatable = templates.filter(t => t.id === undefined);

  // eslint-disable-next-line no-restricted-syntax
  for (const inspection of onlyCreatable) {
    promises.push(create(inspection)());
  }

  return Promise.all(promises);
};

export const updateChecklistTemplates = async (templates: ChecklistTemplateEditable[]) => {
  const promises = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const inspection of templates) {
    // eslint-disable-next-line no-await-in-loop
    const response = await head(inspection.id!)() as Right<Response>;
    promises.push(update(inspection.id!, {
      version: response.right.headers.get('ETag') as string,
      data: inspection,
    })());
  }

  return Promise.all(promises);
};

export const destroyChecklistTemplates = async (templates: ChecklistTemplateEditable[]) => {
  const promises = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const inspection of templates) {
    promises.push(remove(inspection.id!)());
  }

  return Promise.all(promises);
};

export const handleChecklistTemplates = async (templates: ChecklistTemplateEditable[]) => {
  const onlyToCreate = templates.filter(t => t.id === undefined);
  const onlyToUpdate = templates.filter(t => t.id && t.wasChanged);
  const onlyToDelete = templates.filter(t => t.id && t.softDeleted);
  const createChecklistResponses = await createChecklistTemplates(onlyToCreate);

  await destroyChecklistTemplates(onlyToDelete);
  await updateChecklistTemplates(onlyToUpdate);

  return {
    idsSync: [
      ...createChecklistResponses.map(response => {
        return (response as Right<{ id: number }>).right.id;
      }),
      ...templates.filter(t => t.id && !t.softDeleted).map(t => t.id),
    ],
  };
};
